import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Avatar } from '../ui/Avatar';
export function SwipeCard({ user, isTop, onTouchStart, onTouchMove, onTouchEnd, onMouseDown }) {
    const navigate = useNavigate();
    const photos = user.photos?.length
        ? [...user.photos].sort((a, b) => a.order - b.order)
        : user.photoUrl
            ? [{ photoUrl: user.photoUrl }]
            : [];
    const [photoIndex, setPhotoIndex] = useState(0);
    const tapRef = useRef(null);
    const lastTouchRef = useRef({ x: 0, y: 0 });
    const safeIndex = Math.min(photoIndex, Math.max(photos.length - 1, 0));
    const currentPhoto = photos[safeIndex]?.photoUrl || user.photoUrl;
    // Detect taps vs swipes: tap = short duration + small movement
    const handleTouchStartWrapped = useCallback((e) => {
        const t = e.touches[0];
        tapRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
        lastTouchRef.current = { x: t.clientX, y: t.clientY };
        onTouchStart(e);
    }, [onTouchStart]);
    const handleTouchMoveWrapped = useCallback((e) => {
        const t = e.touches[0];
        lastTouchRef.current = { x: t.clientX, y: t.clientY };
        onTouchMove(e);
    }, [onTouchMove]);
    const handleTouchEndWrapped = useCallback(() => {
        if (tapRef.current && photos.length > 1) {
            const elapsed = Date.now() - tapRef.current.time;
            const dx = Math.abs(lastTouchRef.current.x - tapRef.current.x);
            const dy = Math.abs(lastTouchRef.current.y - tapRef.current.y);
            // Only treat as tap if finger barely moved (< 15px) and quick (< 250ms)
            if (elapsed < 250 && dx < 15 && dy < 15) {
                const cardWidth = document.querySelector('.swipe-card')?.offsetWidth || 300;
                const tapX = tapRef.current.x;
                const cardRect = document.querySelector('.swipe-card')?.getBoundingClientRect();
                const relX = tapX - (cardRect?.left || 0);
                if (relX < cardWidth * 0.35) {
                    if (safeIndex > 0) {
                        setPhotoIndex((i) => i - 1);
                        tapRef.current = null;
                        return;
                    }
                }
                else if (relX > cardWidth * 0.65) {
                    if (safeIndex < photos.length - 1) {
                        setPhotoIndex((i) => i + 1);
                        tapRef.current = null;
                        return;
                    }
                }
            }
        }
        tapRef.current = null;
        onTouchEnd();
    }, [onTouchEnd, photos.length, safeIndex]);
    return (_jsxs("div", { className: `swipe-card ${isTop ? '' : 'pointer-events-none'}`, style: { zIndex: isTop ? 10 : 5 }, onTouchStart: isTop ? handleTouchStartWrapped : undefined, onTouchMove: isTop ? handleTouchMoveWrapped : undefined, onTouchEnd: isTop ? handleTouchEndWrapped : undefined, onMouseDown: isTop ? onMouseDown : undefined, children: [photos.length > 1 && (_jsx("div", { className: "absolute top-2.5 left-3 right-3 z-20 flex gap-1", children: photos.map((_, i) => (_jsx("div", { className: "h-[3px] rounded-full flex-1", style: {
                        background: i === safeIndex ? 'rgba(255,255,255,.9)' : 'rgba(255,255,255,.35)',
                    } }, i))) })), currentPhoto ? (_jsx("img", { src: currentPhoto, alt: user.name || '', className: "absolute inset-0 w-full h-full object-cover", draggable: false })) : (_jsx("div", { className: "absolute inset-0 bg-gradient-to-br from-pink-900/40 to-gray-900 flex items-center justify-center", children: _jsx(Avatar, { size: 120 }) })), _jsx("div", { className: "absolute inset-0", style: { background: 'linear-gradient(180deg, transparent 40%, rgba(0,0,0,.85) 100%)' } }), _jsxs("div", { className: "absolute bottom-0 left-0 right-0 p-5 z-10", children: [_jsxs("h3", { className: "text-white text-[22px] font-bold", children: [user.name, user.age ? `, ${user.age}` : ''] }), user.city?.nameRu && (_jsxs("p", { className: "text-white/50 text-[13px] mt-0.5", children: ["\uD83D\uDCCD ", user.city.nameRu] })), user.bio && (_jsx("p", { className: "text-white/75 text-[13px] mt-1 line-clamp-2 leading-relaxed", children: user.bio })), _jsxs("button", { className: "glass-btn mt-3", onClick: (e) => {
                            e.stopPropagation();
                            navigate(`/user/${user.id}`);
                        }, onTouchStart: (e) => e.stopPropagation(), children: [_jsx("span", { className: "glass-btn-shimmer" }), "\u041F\u043E\u0434\u0440\u043E\u0431\u043D\u0435\u0435 ", _jsx(ChevronRight, { className: "w-4 h-4" })] })] })] }));
}
